import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'
import ApplicationCard from './ApplicationCard/ApplicationCard.vue'
import debounce from 'lodash/debounce'
import times from 'lodash/times'
import uniq from 'lodash/uniq'
import { JobApplicationStatuses } from '@/constants/job/jobApplicationStatuses'
import { JobOfferStages } from '@/constants/job/jobOfferStages'
import { SERVICES } from '@/constants/routes'
import responseMixin from '@/mixins/responseMixin'
import JobApplication from '@/models-ts/job/JobApplication'

export default Vue.extend<any, any, any, any>({
  mixins: [responseMixin],
  components: {
    ApplicationCard,
  },
  data() {
    return {
      SERVICES,
      observer: null,
      readedIds: [],
      filter: 'all',
    }
  },
  computed: {
    ...mapGetters({
      job: 'jobDetails/job',
    }),
    selectedApplication() {
      return (this.job.applications || []).find((app: JobApplication) => app.offer?.stage === JobOfferStages.ACCEPTED_BY_CUSTOMER)?.id
    },
    applications() {
      return (this.job.applications || []).filter(
        (app: JobApplication) => app.status === JobApplicationStatuses.NEW || app.id === this.selectedApplication
      )
    },
    allApplications() {
      return this.applications.filter((app: JobApplication) => !app.meta?.archived)
    },
    favouritesApplications() {
      return this.applications.filter((app: JobApplication) => app.meta?.favourite)
    },
    messagedApplications() {
      return this.applications.filter((app: JobApplication) => app.meta?.messaged)
    },
    archivedApplications() {
      return this.job.applications.filter((app: JobApplication) => app.meta?.archived && app.status === JobApplicationStatuses.NEW)
    },
    filteredApplications() {
      switch (this.filter) {
        case 'all':
          return this.allApplications
        case 'favourites':
          return this.favouritesApplications
        case 'messaged':
          return this.messagedApplications
        case 'archive':
          return this.archivedApplications
      }
    },
  },
  watch: {
    'applications.length': {
      handler() {
        if (process.client && this.applications.length) {
          this.$nextTick(() => {
            this.initObserver()
          })
        }
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      readApplications: 'jobDetails/readApplications',
    }),
    initObserver() {
      if (this.$refs.card) {
        const options = {
          root: this.$el,
          rootMargin: '0px',
          threshold: times(10, (n) => n / 10 + 0.1),
        }
        if (!this.observer) {
          this.observer = new IntersectionObserver(this.onVisible, options)
        }
        ;(this.$refs.card || []).forEach((card: any, i: number) => {
          const application: JobApplication = this.applications[i]
          if (!application.isRead && !this.readedIds.includes(application.id)) {
            this.observer.observe(card.$el || card)
          }
        })
      }
    },
    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    },
    onVisible(entries: any) {
      for (const entry of entries) {
        if (entry.intersectionRatio > 0.9) {
          const index = (this.$refs.card || []).findIndex((card: any) => entry.target === (card.$el || card))
          if (this.applications[index] && !this.applications[index].isRead) {
            this.readedIds.push(this.applications[index].id)
            if (this.observer) {
              this.observer.unobserve(entry.target)
            }
          }
        }
      }
      if (this.readedIds.length) {
        this.readDebounced()
      }
    },
    readDebounced: debounce(async function (this: any) {
      const ids = uniq(this.readedIds)
      if (ids.length) {
        this.readedIds = this.readedIds.filter((id: number) => !ids.includes(id))
        try {
          await this.readApplications({ jobId: this.job.id, ids })
        } catch (e) {
          console.error('Error reading application', e)
          this.readedIds.push(...ids)
        }
      }
    }, 1000),
  },
})
